/* Points Nord — objets de gouvernance à nature choisie (process, contrôle,
 * KPI, asset, politique…), hiérarchie illimitée navigable comme un site
 * (fil d'ariane, enfants en cartes), preuve collable sur chaque nœud,
 * tendances entre entrées de données (amélioré / stagné / péjoré). */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import DocAttachTrigger from "@/components/DocAttachTrigger";
import {
  Compass, Plus, X, Loader2, Trash2, TrendingUp, TrendingDown, Minus,
  ChevronRight, ShieldCheck, Gauge, Workflow, Boxes, ScrollText, CircleDot, Pencil,
  AlertTriangle, ListChecks, Target, Building2, Database, ArrowRight,
} from "lucide-react";

const NATURES = {
  process: { label: "Process", icon: Workflow, color: "#378ADD" },
  controle: { label: "Contrôle", icon: ShieldCheck, color: "#1D9E75" },
  kpi: { label: "KPI", icon: Gauge, color: "#EF9F27" },
  asset: { label: "Asset", icon: Boxes, color: "#7F77DD" },
  politique: { label: "Politique", icon: ScrollText, color: "#E24B4A" },
  risque: { label: "Risque", icon: AlertTriangle, color: "#E24B4A" },
  plan: { label: "Plan d'action", icon: ListChecks, color: "#43C6D8" },
  objectif: { label: "Objectif", icon: Target, color: "#EFD127" },
  departement: { label: "Département", icon: Building2, color: "#8FC1EE" },
  data: { label: "Data", icon: Database, color: "#A78BFA" },
  autre: { label: "Autre", icon: CircleDot, color: "#A1A1AA" },
};
const natureMeta = (n) => NATURES[n] || NATURES.autre;

// Libellés FR des relations transversales : [sens actif (out), sens passif (in)]
const REL_LABELS = {
  mitigates: ["atténue", "atténué par"],
  remediates: ["remédie à", "remédié par"],
  measures: ["mesure", "mesuré par"],
  covers: ["couvre", "couvert par"],
  responsible_for: ["responsable de", "sous la responsabilité de"],
  contributes_to: ["contribue à", "alimenté par"],
};
const relLabel = (r) => (REL_LABELS[r.rel_type] || [r.rel_type, r.rel_type])[r.direction === "out" ? 0 : 1];
const TYPE_TO_NATURE_UI = { risk: "risque", action: "plan", outcome: "objectif", control: "controle", process: "process", kpi: "kpi", asset: "asset", policy: "politique", department: "departement", data: "data" };

function TrendBadge({ trend, unit }) {
  if (!trend || trend.state === "none") return null;
  const map = {
    improved: { Icon: TrendingUp, color: "#1D9E75", label: "Amélioré" },
    stagnant: { Icon: Minus, color: "#A1A1AA", label: "Stable" },
    worsened: { Icon: TrendingDown, color: "#E24B4A", label: "Péjoré" },
  };
  const m = map[trend.state];
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border"
      style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}12` }}
      title={trend.delta != null ? `Δ ${trend.delta > 0 ? "+" : ""}${trend.delta} ${unit || ""}` : ""}
      data-testid="np-trend-badge">
      <m.Icon className="w-3 h-3" /> {m.label}{trend.delta != null && ` (${trend.delta > 0 ? "+" : ""}${trend.delta})`}
    </span>
  );
}

function Sparkline({ series }) {
  if (!series || series.length < 2) return null;
  const min = Math.min(...series), max = Math.max(...series);
  const range = max - min || 1;
  return (
    <span className="inline-flex items-end gap-[2px] h-5" data-testid="np-sparkline">
      {series.map((v, i) => (
        <span key={i} className="w-1 rounded-sm bg-[#378ADD]/70"
          style={{ height: `${20 + 80 * ((v - min) / range)}%` }} />
      ))}
    </span>
  );
}

function PointForm({ initial, parentId, onClose, onSaved }) {
  const [f, setF] = useState({
    name: initial?.name || "", nature: initial?.nature || "process",
    description: initial?.description || "", owner: initial?.owner || "",
    kpi_unit: initial?.kpi_unit || "", kpi_target: initial?.kpi_target ?? "",
    kpi_direction: initial?.kpi_direction || "up",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { notify.error("Nom requis."); return; }
    setBusy(true);
    const body = { ...f, kpi_target: f.kpi_target === "" ? null : Number(f.kpi_target) };
    try {
      if (initial?.id) await api.patch(`/workspace/north-points/${initial.id}`, body);
      else await api.post("/workspace/north-points", { ...body, parent_id: parentId || null });
      onSaved();
    } catch (e) { notify.error(fmtError(e, "Sauvegarde impossible.")); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="np-form-modal">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-zinc-100">{initial?.id ? "Modifier le Point Nord" : parentId ? "Nouvel enfant" : "Nouveau Point Nord"}</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nom" className="noam-input w-full" data-testid="np-form-name" />
        <div className="flex gap-1 flex-wrap" data-testid="np-form-natures">
          {Object.entries(NATURES).map(([k, m]) => (
            <button key={k} onClick={() => setF({ ...f, nature: k })}
              className={`text-[11px] px-2.5 py-1 rounded-md border inline-flex items-center gap-1 transition-colors ${f.nature === k ? "font-bold" : "opacity-60 hover:opacity-100"}`}
              style={{ color: m.color, borderColor: f.nature === k ? m.color : "rgba(255,255,255,0.1)" }}
              data-testid={`np-form-nature-${k}`}>
              <m.icon className="w-3 h-3" /> {m.label}
            </button>
          ))}
        </div>
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3}
          placeholder="Description…" className="noam-input w-full text-[12px]" data-testid="np-form-desc" />
        <input value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} placeholder="Responsable (optionnel)" className="noam-input w-full" data-testid="np-form-owner" />
        {f.nature === "kpi" && (
          <div className="grid grid-cols-3 gap-2">
            <input value={f.kpi_target} onChange={(e) => setF({ ...f, kpi_target: e.target.value })} placeholder="Cible" type="number" className="noam-input" data-testid="np-form-target" />
            <input value={f.kpi_unit} onChange={(e) => setF({ ...f, kpi_unit: e.target.value })} placeholder="Unité (%, j, CHF…)" className="noam-input" data-testid="np-form-unit" />
            <select value={f.kpi_direction} onChange={(e) => setF({ ...f, kpi_direction: e.target.value })} className="noam-input" data-testid="np-form-direction">
              <option value="up">Plus haut = mieux</option>
              <option value="down">Plus bas = mieux</option>
            </select>
          </div>
        )}
        <div className="text-right">
          <button onClick={save} disabled={busy} className="btn-primary text-[12px] inline-flex items-center gap-1.5" data-testid="np-form-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function PointCard({ p, onOpen }) {
  const m = natureMeta(p.nature);
  return (
    <button onClick={() => onOpen(p.id)}
      className="noam-card p-4 text-left hover:border-[#7F77DD]/40 transition-colors space-y-2 w-full"
      data-testid={`np-card-${p.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 min-w-0">
          <m.icon className="w-4 h-4 shrink-0" style={{ color: m.color }} />
          <span className="text-[13px] font-semibold text-zinc-100 truncate">{p.name}</span>
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full border shrink-0"
          style={{ color: m.color, borderColor: `${m.color}44` }}>{m.label}</span>
      </div>
      <div className="flex items-center gap-3 text-[11px] text-zinc-500 flex-wrap">
        {p.children_count > 0 && <span>{p.children_count} enfant(s)</span>}
        {p.evidence_count > 0 && <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#1D9E75]" /> {p.evidence_count} preuve(s)</span>}
        {p.last_value != null && <span className="text-zinc-300 font-semibold">{p.last_value}{p.kpi_unit ? ` ${p.kpi_unit}` : ""}{p.kpi_target != null && <span className="text-zinc-600 font-normal"> / cible {p.kpi_target}</span>}</span>}
        <TrendBadge trend={p.trend} unit={p.kpi_unit} />
        <Sparkline series={p.trend?.series} />
      </div>
    </button>
  );
}

function PointDetail({ pid, onNavigate }) {
  const [pt, setPt] = useState(null);
  const [form, setForm] = useState(null); // {parentId} | {initial}
  const [entryVal, setEntryVal] = useState("");
  const [entryNote, setEntryNote] = useState("");
  const load = useCallback(() => {
    api.get(`/workspace/north-points/${pid}`).then((r) => setPt(r.data))
      .catch((e) => notify.error(fmtError(e, "Chargement impossible.")));
  }, [pid]);
  useEffect(() => { load(); }, [load]);
  const addEntry = async () => {
    if (entryVal === "") return;
    try {
      await api.post(`/workspace/north-points/${pid}/entries`, { value: Number(entryVal), note: entryNote });
      setEntryVal(""); setEntryNote("");
      load();
    } catch (e) { notify.error(fmtError(e, "Entrée impossible.")); }
  };
  const del = async () => {
    const cascade = (pt.children || []).length > 0;
    if (!window.confirm(cascade ? `Supprimer « ${pt.name} » ET ses ${pt.children.length} enfant(s) (et petits-enfants) ?` : `Supprimer « ${pt.name} » ?`)) return;
    try {
      await api.delete(`/workspace/north-points/${pid}?cascade=${cascade}`);
      onNavigate(pt.parent_id || null);
    } catch (e) { notify.error(fmtError(e, "Suppression impossible.")); }
  };
  if (!pt) return <div className="py-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>;
  const m = natureMeta(pt.nature);
  return (
    <div className="space-y-4" data-testid="np-detail">
      <div className="text-[11px] text-zinc-500 flex items-center gap-1 flex-wrap" data-testid="np-breadcrumb">
        <button onClick={() => onNavigate(null)} className="hover:text-white" data-testid="np-crumb-root">Points Nord</button>
        {(pt.breadcrumb || []).map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1">
            <ChevronRight className="w-3 h-3" />
            <button onClick={() => onNavigate(c.id)} className="hover:text-white" data-testid={`np-crumb-${c.id}`}>{c.name}</button>
          </span>
        ))}
        <ChevronRight className="w-3 h-3" /><span className="text-zinc-300">{pt.name}</span>
      </div>
      {(pt.also_part_of || []).length > 0 && (
        <div className="text-[11px] text-zinc-500" data-testid="np-also-part-of">
          Présent aussi dans :{" "}
          {pt.also_part_of.map((p) => (
            <button key={p.id} onClick={() => onNavigate(p.id)} className="text-[#8FC1EE] hover:underline mr-2" data-testid={`np-also-${p.id}`}>{p.name}</button>
          ))}
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md border flex items-center justify-center shrink-0" style={{ borderColor: `${m.color}44`, background: `${m.color}12` }}>
            <m.icon className="w-5 h-5" style={{ color: m.color }} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-zinc-100">{pt.name}</h2>
            <div className="text-[11px] text-zinc-500">{m.label}{pt.owner ? ` · ${pt.owner}` : ""} · {pt.status}</div>
            {pt.description && <p className="text-[12px] text-zinc-400 max-w-2xl mt-1">{pt.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <DocAttachTrigger module="north_point" entityId={pt.id} entityLabel={pt.name} />
          <button onClick={() => setForm({ initial: pt })} className="p-1.5 rounded-md border border-white/10 text-zinc-400 hover:text-white" title="Modifier" data-testid="np-edit-btn"><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={del} className="p-1.5 rounded-md border border-white/10 text-zinc-500 hover:text-[#E24B4A]" title="Supprimer" data-testid="np-delete-btn"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* Valeurs & tendance */}
      <div className="noam-card p-4 space-y-3" data-testid="np-values-panel">
        <div className="flex items-center gap-4 flex-wrap">
          {pt.last_value != null && (
            <span className="text-2xl font-bold text-zinc-100">{pt.last_value}<span className="text-[12px] text-zinc-500 font-normal"> {pt.kpi_unit}</span></span>
          )}
          {pt.kpi_target != null && <span className="text-[12px] text-zinc-500">Cible : <span className="text-zinc-200 font-semibold">{pt.kpi_target} {pt.kpi_unit}</span></span>}
          <TrendBadge trend={pt.trend} unit={pt.kpi_unit} />
          <Sparkline series={pt.trend?.series} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={entryVal} onChange={(e) => setEntryVal(e.target.value)} type="number" placeholder="Nouvelle valeur"
            className="noam-input w-36 text-[12px]" data-testid="np-entry-value" />
          <input value={entryNote} onChange={(e) => setEntryNote(e.target.value)} placeholder="Note (optionnel)"
            className="noam-input flex-1 min-w-[140px] text-[12px]" data-testid="np-entry-note" />
          <button onClick={addEntry} className="btn-primary text-[11px] py-1.5 px-3" data-testid="np-entry-add">Ajouter l'entrée</button>
        </div>
        {(pt.entries || []).length > 0 && (
          <div className="max-h-32 overflow-y-auto space-y-0.5" data-testid="np-entries-list">
            {pt.entries.map((e) => (
              <div key={e.id} className="text-[11px] text-zinc-500 flex items-center gap-2">
                <span className="font-mono text-zinc-300">{e.value}{pt.kpi_unit ? ` ${pt.kpi_unit}` : ""}</span>
                <span>{new Date(e.at).toLocaleString("fr-CH")}</span>
                {e.note && <span className="italic truncate">— {e.note}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vue consolidée (remontées type Smart Cockpit sur le sous-arbre) */}
      {pt.rollup?.count > 0 && (
        <div className="noam-card p-4" data-testid="np-rollup">
          <div className="noam-overline mb-2">Vue consolidée — {pt.rollup.count} objet(s) dans le sous-arbre</div>
          {pt.rollup.with_value > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Valeur moyenne</div>
                <div className="text-xl font-semibold text-white" data-testid="np-rollup-avg">{pt.rollup.avg}</div>
                <div className="text-[10px] text-zinc-600">{pt.rollup.with_value} objet(s) mesurés</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">État le plus haut</div>
                <div className="text-sm font-semibold text-[#1D9E75]">{pt.rollup.max}</div>
                <button onClick={() => onNavigate(pt.rollup.best.id)} className="text-[10px] text-[#8FC1EE] hover:underline truncate block max-w-full" data-testid="np-rollup-best">{pt.rollup.best.label}</button>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">État le plus bas</div>
                <div className="text-sm font-semibold text-[#E24B4A]">{pt.rollup.min}</div>
                <button onClick={() => onNavigate(pt.rollup.worst.id)} className="text-[10px] text-[#8FC1EE] hover:underline truncate block max-w-full" data-testid="np-rollup-worst">{pt.rollup.worst.label}</button>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Tendances</div>
                <div className="flex items-center gap-2 mt-1 text-[11px]">
                  <span className="text-[#1D9E75] flex items-center gap-0.5"><TrendingUp className="w-3 h-3" />{pt.rollup.trends.improved}</span>
                  <span className="text-zinc-400 flex items-center gap-0.5"><Minus className="w-3 h-3" />{pt.rollup.trends.stagnant}</span>
                  <span className="text-[#E24B4A] flex items-center gap-0.5"><TrendingDown className="w-3 h-3" />{pt.rollup.trends.worsened}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-zinc-500">Aucune valeur saisie dans le sous-arbre — ajoutez des valeurs sur les objets pour activer les remontées.</div>
          )}
          {(pt.rollup.by_type || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-white/5">
              {pt.rollup.by_type.map((b) => {
                const bm = natureMeta(TYPE_TO_NATURE_UI[b.type] || "autre");
                return (
                  <span key={b.type} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-zinc-400 flex items-center gap-1">
                    <bm.icon className="w-3 h-3" style={{ color: bm.color }} />
                    {bm.label} × {b.count}{b.avg != null ? ` · moy. ${b.avg}` : ""}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Relations ontologiques transversales — navigation clic / retour */}
      {(pt.relations || []).length > 0 && (
        <div className="noam-card p-4" data-testid="np-relations">
          <div className="noam-overline mb-2">Relations</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {pt.relations.map((r) => {
              const rm = natureMeta(TYPE_TO_NATURE_UI[r.type] || "autre");
              return (
                <button key={`${r.rel_type}-${r.id}-${r.direction}`} onClick={() => onNavigate(r.id)}
                  className="flex items-center gap-2 text-left px-3 py-2 rounded-md border border-white/8 hover:border-white/25 bg-white/[0.02] transition-colors group"
                  data-testid={`np-rel-${r.id}`}>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-white/10 text-zinc-500 shrink-0">{relLabel(r)}</span>
                  <rm.icon className="w-3.5 h-3.5 shrink-0" style={{ color: rm.color }} />
                  <span className="text-[12px] text-zinc-300 group-hover:text-white truncate flex-1">{r.label}</span>
                  <ArrowRight className="w-3 h-3 text-zinc-700 group-hover:text-[#8FC1EE] shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Points de norme couverts */}
      {(pt.norm_points_covered || []).length > 0 && (
        <div className="noam-card p-4" data-testid="np-norm-coverage">
          <div className="noam-overline mb-2">Points de norme couverts</div>
          <div className="flex flex-wrap gap-1.5">
            {pt.norm_points_covered.map((np) => (
              <span key={np.id} className="text-[10px] px-2 py-0.5 rounded-full border border-[#378ADD]/30 bg-[#378ADD]/10 text-[#8FC1EE]">
                {np.framework_name} · {np.code || np.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Enfants */}
      <div className="flex items-center justify-between">
        <div className="noam-overline">Enfants ({(pt.children || []).length})</div>
        <button onClick={() => setForm({ parentId: pt.id })} className="text-[11px] px-3 py-1.5 rounded-md border border-[#7F77DD]/40 text-[#B9B3EC] hover:bg-[#7F77DD]/10 inline-flex items-center gap-1.5" data-testid="np-add-child-btn">
          <Plus className="w-3 h-3" /> Ajouter un enfant
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {(pt.children || []).map((c) => <PointCard key={c.id} p={c} onOpen={onNavigate} />)}
        {(pt.children || []).length === 0 && <div className="text-[11px] text-zinc-600 italic col-span-full">Aucun enfant — descendez la structure en ajoutant des sous-points.</div>}
      </div>
      {form && <PointForm initial={form.initial} parentId={form.parentId} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

export default function NorthPoints() {
  const [searchParams, setSearchParams] = useSearchParams();
  const current = searchParams.get("np") || null;
  const navigateTo = (id) => setSearchParams(id ? { np: id } : {});
  const [roots, setRoots] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => {
    api.get("/workspace/north-points").then((r) => setRoots(r.data.items || []))
      .catch((e) => notify.error(fmtError(e, "Chargement impossible.")));
  }, []);
  useEffect(() => { if (!current) load(); }, [current, load]);

  if (current) return <PointDetail pid={current} onNavigate={navigateTo} />;
  return (
    <div className="space-y-4" data-testid="north-points-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="noam-overline">Gouvernance · Structure navigable</div>
          <h2 className="text-lg font-semibold text-zinc-100 inline-flex items-center gap-2">
            <Compass className="w-5 h-5 text-[#7F77DD]" /> Points Nord
          </h2>
          <p className="text-[12px] text-zinc-500">Process, contrôles, KPI, assets… avec enfants et petits-enfants, preuves sur chaque nœud et tendances entre entrées.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary inline-flex items-center gap-2" data-testid="np-add-root-btn">
          <Plus className="w-4 h-4" /> Nouveau Point Nord
        </button>
      </div>
      {roots === null ? (
        <div className="py-10 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…</div>
      ) : roots.length === 0 ? (
        <div className="noam-card p-8 text-center text-zinc-500 text-sm" data-testid="np-empty">
          Aucun Point Nord. Créez votre premier nœud (process, contrôle, KPI, asset…) puis descendez la structure avec des enfants et petits-enfants.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {roots.map((p) => <PointCard key={p.id} p={p} onOpen={navigateTo} />)}
        </div>
      )}
      {showForm && <PointForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
